import { z } from 'zod';

import {
  CHANNEL_STATE_REASON_CODES,
  CLOSING_REASON_CODES,
  CONFIRMATION_REASON_CODES,
  EMERGENCY_STOP_REASON_CODES,
  EXECUTION_REASON_CODES,
  PLATFORM_REASON_CODES,
  SETTINGS_REASON_CODES,
  TELEGRAM_REASON_CODES,
  TRACKING_REASON_CODES,
} from './reason-codes/catalogue-codes';
import {
  AUTH_REASON_CODES,
  CHANNEL_REASON_CODES,
  POSITION_REASON_CODES,
  REQUEST_REASON_CODES,
  SETTINGS_ROUTE_REASON_CODES,
  TELEGRAM_LOGIN_REASON_CODES,
  TRADING_ACCOUNT_REASON_CODES,
} from './reason-codes/contract-codes';

/** Codes created by S1 for the health route and the single error handler. */
export const CORE_REASON_CODES = ['INTERNAL_ERROR', 'VALIDATION_FAILED', 'NOT_FOUND'] as const;

/**
 * Every stable reason code (master X2).
 *
 * Extension point (C1 spec §5.4): C2 adds its groups to this spread. A rename is a contract
 * change that updates this list, the i18n catalogue and openapi.json in one PR.
 */
export const REASON_CODES = [
  ...CORE_REASON_CODES,
  ...AUTH_REASON_CODES,
  ...REQUEST_REASON_CODES,
  ...TELEGRAM_LOGIN_REASON_CODES,
  ...TRADING_ACCOUNT_REASON_CODES,
  ...CHANNEL_REASON_CODES,
  ...POSITION_REASON_CODES,
  ...SETTINGS_ROUTE_REASON_CODES,
  // C2 groups (specs/C2-reason-codes/spec.md §4); C1's groups above stay first and unchanged.
  ...PLATFORM_REASON_CODES,
  ...TELEGRAM_REASON_CODES,
  ...CHANNEL_STATE_REASON_CODES,
  ...SETTINGS_REASON_CODES,
  ...CONFIRMATION_REASON_CODES,
  ...EMERGENCY_STOP_REASON_CODES,
  ...EXECUTION_REASON_CODES,
  ...TRACKING_REASON_CODES,
  ...CLOSING_REASON_CODES,
] as const;

/** Stable reason code carried by every failure and warning; translated with `t('reason.<code>')`. */
export const ReasonCodeSchema = z.enum(REASON_CODES);
/** Reason code. */
export type ReasonCode = z.infer<typeof ReasonCodeSchema>;

/**
 * Narrows an untrusted value (API body, bot payload) to a {@link ReasonCode}.
 *
 * @param value - Anything received from outside.
 * @returns `true` when `value` is one of the enum values.
 * @example
 * if (isReasonCode(body.reasonCode)) { … }
 */
export function isReasonCode(value: unknown): value is ReasonCode {
  return ReasonCodeSchema.safeParse(value).success;
}

/**
 * Subset of {@link ReasonCodeSchema} for a closed position's reason in E9 (C2 spec R14). It is
 * the type of C1's `ClosedPosition.closeReason` itself (C1 decision 1, 14/10): no mapping.
 *
 * @example
 * ClosingReasonSchema.parse('CLOSED_BY_STOP_LOSS');
 */
export const ClosingReasonSchema = ReasonCodeSchema.extract(CLOSING_REASON_CODES);

/** A closing reason; see {@link ClosingReasonSchema}. */
export type ClosingReason = z.infer<typeof ClosingReasonSchema>;
